import { useEffect, useState } from "react";
import { listTestimonials, fileUrl } from "../lib/api";
import { Star, Quote, MapPin, Zap } from "lucide-react";
import { Reveal } from "./Reveal";

export function Stars({ rating, className = "w-4 h-4" }) {
  return (
    <div className="flex gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`${className} ${n <= rating ? "fill-primary text-primary" : "text-border"}`}
          strokeWidth={1.5}
        />
      ))}
    </div>
  );
}

function Avatar({ name, photo }) {
  if (photo) return <img src={fileUrl(photo)} alt={name} className="w-12 h-12 rounded-full object-cover" />;
  const initials = name.split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  return (
    <div className="w-12 h-12 rounded-full bg-secondary text-white flex items-center justify-center font-heading font-bold">
      {initials}
    </div>
  );
}

export function TestimonialsSection() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    listTestimonials().then(setItems).catch(console.error);
  }, []);

  if (items.length === 0) return null;
  const avg = (items.reduce((s, t) => s + t.rating, 0) / items.length).toFixed(1);

  return (
    <section data-testid="testimonials-section" className="bg-secondary text-white py-12 sm:py-20 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="mb-8 sm:mb-12 flex flex-wrap items-end justify-between gap-4 sm:gap-6">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-3">Customer Stories</div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight leading-none">
              Trusted by families and businesses across Vidarbha.
            </h2>
          </div>
          <div data-testid="testimonials-avg" className="flex items-center gap-3 bg-white/10 border border-white/15 rounded-2xl px-5 py-3">
            <div className="font-heading text-3xl font-bold">{avg}</div>
            <div>
              <Stars rating={Math.round(Number(avg))} />
              <div className="text-xs text-white/70 mt-1">{items.length} customer review{items.length > 1 ? "s" : ""}</div>
            </div>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {items.map((t, i) => (
            <Reveal
              key={t.id}
              delay={(i % 3) * 0.12}
              data-testid={`testimonial-${t.id}`}
              className="relative bg-white/[0.06] border border-white/10 rounded-2xl p-5 sm:p-7 flex flex-col hover:bg-white/[0.1] transition-colors"
            >
              <Quote className="absolute top-6 right-6 w-8 h-8 text-primary/40" strokeWidth={1.25} />
              <Stars rating={t.rating} />
              <p className="mt-4 text-white/85 leading-relaxed flex-1">"{t.text}"</p>
              <div className="mt-6 pt-5 border-t border-white/10 flex items-center gap-3">
                <Avatar name={t.name} photo={t.photo_url} />
                <div className="min-w-0">
                  <div className="font-heading font-semibold leading-tight truncate">{t.name}</div>
                  <div className="flex flex-wrap gap-x-3 text-xs text-white/60 mt-1">
                    {t.location && (
                      <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {t.location}</span>
                    )}
                    {t.system_kw && (
                      <span className="inline-flex items-center gap-1"><Zap className="w-3 h-3 text-primary" /> {t.system_kw}</span>
                    )}
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
